import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Upload, Download, Trash2, Plus, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal that lets admin manage a single customer's:
 *  - credit limit
 *  - per-product / per-category discount overrides
 *  - Excel upload / template download
 */
const CustomerOverridesDialog = ({ customer, open, onClose, onSaved }) => {
  const [creditLimit, setCreditLimit] = useState(customer?.credit_limit || 0);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ type: 'product', identifier: '', discount_pct: 0 });
  const [replace, setReplace] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !customer) return;
    setCreditLimit(customer.credit_limit || 0);
    setForm({ type: 'product', identifier: '', discount_pct: 0 });
    loadOverrides();
  }, [open, customer?.id]);

  const loadOverrides = async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/customers/${customer.id}/overrides`);
      setOverrides(r.data);
    } catch (e) {
      toast.error('Failed to load overrides');
    } finally {
      setLoading(false);
    }
  };

  const saveCreditLimit = async () => {
    try {
      const r = await api.put(`/admin/customers/${customer.id}`, { credit_limit: Number(creditLimit) });
      toast.success('Credit limit saved');
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const addOverride = async () => {
    if (!form.identifier.trim()) {
      toast.error('Identifier (SKU or category slug) is required');
      return;
    }
    try {
      await api.post(`/admin/customers/${customer.id}/overrides`, {
        type: form.type,
        identifier: form.identifier.trim(),
        discount_pct: Number(form.discount_pct),
      });
      toast.success('Override added');
      setForm({ type: 'product', identifier: '', discount_pct: 0 });
      loadOverrides();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    }
  };

  const deleteOverride = async (oid) => {
    try {
      await api.delete(`/admin/customers/${customer.id}/overrides/${oid}`);
      setOverrides(prev => prev.filter(o => o.id !== oid));
      toast.success('Removed');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('bq_token');
      const r = await fetch(`${BACKEND_URL}/api/admin/customers/${customer.id}/overrides/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `overrides-${customer.email}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const uploadExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(
        `/admin/customers/${customer.id}/overrides/upload-excel?replace=${replace}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Imported ${r.data.inserted} override(s)`);
      if (r.data.credit_limit !== null && r.data.credit_limit !== undefined) {
        setCreditLimit(r.data.credit_limit);
      }
      loadOverrides();
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail?.errors)) {
        toast.error(`${detail.errors.length} issue(s): ${detail.errors[0]}`);
      } else {
        toast.error(detail || 'Upload failed');
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!open || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="customer-overrides-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-[#12263A]">Pricing &amp; Credit — {customer.name}</h2>
            <p className="text-xs text-neutral-500">{customer.email} · {customer.user_type}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1 hover:bg-neutral-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Credit limit */}
          <div>
            <div className="text-xs font-bold uppercase text-[#F46A25] mb-2 flex items-center gap-1"><Wallet className="w-3 h-3" /> Credit limit</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-600">£</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={creditLimit}
                onChange={e => setCreditLimit(e.target.value)}
                className="flex-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
                data-testid="credit-limit-input"
              />
              <button
                onClick={saveCreditLimit}
                className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm"
                data-testid="save-credit-limit-btn"
              >
                Save
              </button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">Set to 0 to disable pay-on-credit for this user.</p>
          </div>

          {/* Excel bulk import */}
          <div className="border-t border-neutral-200 pt-4">
            <div className="text-xs font-bold uppercase text-[#F46A25] mb-2">Bulk import (Excel)</div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={downloadTemplate}
                className="text-xs bg-[#12263A] hover:bg-black text-white font-bold px-3 py-2 rounded-sm flex items-center gap-1"
                data-testid="download-overrides-template-btn"
              >
                <Download className="w-3 h-3" /> Download template
              </button>
              <input
                type="file"
                accept=".xlsx"
                ref={fileRef}
                onChange={uploadExcel}
                className="hidden"
                data-testid="overrides-upload-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="text-xs bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-3 py-2 rounded-sm flex items-center gap-1 disabled:opacity-60"
                data-testid="upload-overrides-btn"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                Upload Excel
              </button>
              <label className="text-[11px] text-neutral-600 flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} className="accent-[#F46A25]" />
                Replace existing on upload
              </label>
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">
              Template pre-fills the current credit limit at cell B1. Columns: <b>Type</b> (product|category), <b>Identifier</b> (SKU or category slug), <b>Discount %</b>, <b>Fixed Price £</b> (product-only), <b>Bulk Qty</b> (product-only), <b>Bulk Price £</b> (product-only). Set Bulk Qty + Bulk Price together, or leave both blank.
            </p>
          </div>

          {/* Manual add */}
          <div className="border-t border-neutral-200 pt-4">
            <div className="text-xs font-bold uppercase text-[#F46A25] mb-2">Add / update a single override</div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
                data-testid="override-type-select"
              >
                <option value="product">Product (by SKU)</option>
                <option value="category">Category (by slug)</option>
              </select>
              <input
                type="text"
                placeholder={form.type === 'product' ? 'SKU e.g. DW-18V-001' : 'slug e.g. tools'}
                value={form.identifier}
                onChange={e => setForm(f => ({ ...f, identifier: e.target.value }))}
                className="border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25] sm:col-span-2"
                data-testid="override-identifier-input"
              />
              <div className="flex gap-1">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="%"
                  value={form.discount_pct}
                  onChange={e => setForm(f => ({ ...f, discount_pct: e.target.value }))}
                  className="w-20 border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
                  data-testid="override-discount-input"
                />
                <button
                  onClick={addOverride}
                  className="flex-1 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-xs px-2 rounded-sm flex items-center justify-center gap-1"
                  data-testid="add-override-btn"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            </div>
          </div>

          {/* Existing overrides */}
          <div className="border-t border-neutral-200 pt-4">
            <div className="text-xs font-bold uppercase text-[#F46A25] mb-2">Current overrides ({overrides.length})</div>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#F46A25]" /></div>
            ) : overrides.length === 0 ? (
              <div className="text-sm text-neutral-500 py-4 text-center border border-dashed border-neutral-300 rounded-sm">
                No overrides yet. This user pays the standard tier price.
              </div>
            ) : (
              <div className="border border-neutral-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-600">
                    <tr>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Identifier</th>
                      <th className="text-right px-3 py-2">Discount</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="overrides-list">
                    {overrides.map(o => (
                      <tr key={o.id} className="border-t border-neutral-100">
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${o.type === 'product' ? 'bg-orange-100 text-[#F46A25]' : 'bg-blue-100 text-blue-700'}`}>{o.type}</span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{o.identifier}</td>
                        <td className="px-3 py-2 text-right font-bold">{o.discount_pct}%</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => deleteOverride(o.id)}
                            className="text-red-600 hover:bg-red-50 p-1 rounded"
                            aria-label="Delete override"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-2">
              Precedence: product SKU override &gt; category override &gt; product&apos;s tier discount.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOverridesDialog;
